import { useState, useEffect } from "react";
import { downloadCsaf, fetchCve, fetchCvePrevalence, fetchCves, fetchRecentBuckets, fetchSimilar } from "../api";
import ReferencesPanel from "./ReferencesPanel";
import CpesPanel from "./CpesPanel";
import PrevalencePanel from "./PrevalencePanel";
import CvssRadarChart from "./RadarChart";
import Cvss4RadarChart from "./Cvss4RadarChart";
import SsvcRadarChart from "./SsvcRadarChart";
import DetailsBody from "./DetailsBody";
import { getSourceColor, getSourceLabel, VINNIE_LLM_KEY } from "../utils/cvss";
import { fmtTs, fmtDate } from "../utils/time";

function relTime(ts) {
  if (!ts) return "";
  const t = new Date(ts);
  if (isNaN(t)) return "";
  const diffMs = Date.now() - t.getTime();
  const day = 86400000;
  const days = Math.floor(diffMs / day);
  if (days < 1) {
    const hrs = Math.floor(diffMs / 3600000);
    if (hrs < 1) return "just now";
    return `${hrs}h ago`;
  }
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function findLLMScore(scores, prefix) {
  if (!scores) return null;
  for (const [k, v] of Object.entries(scores)) {
    if (k.toLowerCase().startsWith(`llm/${prefix}`) && typeof v?.base_score === "number") {
      return { key: k, score: v.base_score };
    }
  }
  return null;
}

function llmAgreement(scores) {
  const gemma = findLLMScore(scores, "gemma");
  const nemotron = findLLMScore(scores, "nemotron");
  if (!gemma || !nemotron) return null;
  const diff = Math.abs(gemma.score - nemotron.score);
  let color, label;
  if (diff === 0) {
    color = "#3fb950"; label = "Ready — both models match";
  } else if (diff < 1) {
    color = "#d29922"; label = `Close — ${diff.toFixed(1)} manions apart`;
  } else if (diff <= 2) {
    color = "#f0883e"; label = `Disagreement — ${diff.toFixed(1)} manions apart`;
  } else {
    color = "#f85149"; label = `Strong disagreement — ${diff.toFixed(1)} manions apart`;
  }
  return { color, label, diff, gemma, nemotron };
}

function AgreementIndicator({ scores }) {
  const a = llmAgreement(scores);
  if (!a) return null;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "8px 12px", marginBottom: 8,
      background: "var(--bg-secondary)", borderRadius: 6,
      border: `1px solid ${a.color}`, fontSize: 12,
    }}>
      <span style={{
        width: 10, height: 10, borderRadius: "50%",
        background: a.color, display: "inline-block", flexShrink: 0,
      }} />
      <span style={{ fontWeight: 600, color: a.color }}>LLM Agreement:</span>
      <span style={{ color: "var(--text-secondary)" }}>{a.label}</span>
      <span style={{ color: "var(--text-muted)", marginLeft: "auto", fontFamily: "monospace" }}>
        {a.gemma.key.replace("LLM/", "")}: {a.gemma.score} · {a.nemotron.key.replace("LLM/", "")}: {a.nemotron.score}
      </span>
    </div>
  );
}

// ── SSVC Indicators ────────────────────────────────────────────────────────

const SSVC_DECISION_LEVEL = { "Track": 0, "Track*": 1, "Attend": 2, "Act": 3 };

function ssvcDecisionFromEntry(entry) {
  if (!entry) return null;
  const comps = entry.components || [];
  const p = comps.find(c => c.metric === "P");
  if (p?.short && SSVC_DECISION_LEVEL[p.short] !== undefined) return p.short;
  const e = normalizeSsvcCell("E", comps.find(c => c.metric === "E")?.short);
  const a = normalizeSsvcCell("A", comps.find(c => c.metric === "A")?.short);
  const t = normalizeSsvcCell("T", comps.find(c => c.metric === "T")?.short);
  return SSVC_OUTCOMES[`${e}|${a}|${t}`] || null;
}

function findSsvcEntry(scores, sourcePrefix, modelPrefix) {
  if (!scores) return null;
  for (const [k, v] of Object.entries(scores)) {
    const lk = k.toLowerCase();
    const okSource = lk.startsWith(sourcePrefix.toLowerCase() + "/");
    const okModel = !modelPrefix || lk.includes(modelPrefix.toLowerCase());
    if (okSource && okModel) {
      const dec = ssvcDecisionFromEntry(v);
      if (dec) return { key: k, decision: dec, level: SSVC_DECISION_LEVEL[dec] };
    }
  }
  return null;
}

function ssvcLLMAgreement(scores) {
  const gemma = findSsvcEntry(scores, "LLM", "gemma");
  const nemotron = findSsvcEntry(scores, "LLM", "nemotron");
  if (!gemma || !nemotron) return null;
  const diff = Math.abs(gemma.level - nemotron.level);
  let color, label;
  if (diff === 0) { color = "#3fb950"; label = `Ready — both pick ${gemma.decision}`; }
  else if (diff === 1) { color = "#d29922"; label = `Close — ${gemma.decision} vs ${nemotron.decision}`; }
  else if (diff === 2) { color = "#f0883e"; label = `Disagreement — ${gemma.decision} vs ${nemotron.decision}`; }
  else { color = "#f85149"; label = `Strong disagreement — ${gemma.decision} vs ${nemotron.decision}`; }
  return { color, label, gemma, nemotron };
}

function SsvcAgreementIndicator({ scores }) {
  const a = ssvcLLMAgreement(scores);
  if (!a) return null;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "8px 12px", marginBottom: 8,
      background: "var(--bg-secondary)", borderRadius: 6,
      border: `1px solid ${a.color}`, fontSize: 12,
    }}>
      <span style={{ width: 10, height: 10, borderRadius: "50%", background: a.color, display: "inline-block", flexShrink: 0 }} />
      <span style={{ fontWeight: 600, color: a.color }}>LLM Agreement:</span>
      <span style={{ color: "var(--text-secondary)" }}>{a.label}</span>
      <span style={{ color: "var(--text-muted)", marginLeft: "auto", fontFamily: "monospace" }}>
        {a.gemma.key.replace("LLM/", "")}: {a.gemma.decision} · {a.nemotron.key.replace("LLM/", "")}: {a.nemotron.decision}
      </span>
    </div>
  );
}

// Surface disagreements on the Exploitation metric (N/P/A), with special
// emphasis on PoC since it's the actionable middle ground.
function SsvcExploitationIndicator({ scores }) {
  if (!scores) return null;
  const E_LEVEL = { N: 0, P: 1, A: 2 };
  const E_LABEL = { N: "None", P: "PoC", A: "Active" };

  // Collect E value per source
  const eByModel = {};
  for (const [k, v] of Object.entries(scores)) {
    const e = (v.components || []).find(c => c.metric === "E");
    if (e?.short) {
      const norm = normalizeSsvcCell("E", e.short);
      if (E_LEVEL[norm] !== undefined) eByModel[k] = norm;
    }
  }
  const values = Object.values(eByModel);
  if (values.length < 2) return null;
  const unique = [...new Set(values)];
  if (unique.length === 1) return null;  // all agree, nothing to flag

  // Group sources by their E value
  const groups = {};
  for (const [src, val] of Object.entries(eByModel)) {
    if (!groups[val]) groups[val] = [];
    groups[val].push(src);
  }
  const has = { N: groups.N?.length > 0, P: groups.P?.length > 0, A: groups.A?.length > 0 };

  let color, label;
  if (has.N && has.A) {
    color = "#f85149";
    label = "Major split — sources disagree between None and Active exploitation";
  } else if (has.P && has.A) {
    color = "#f0883e";
    label = "PoC vs Active — confirm whether exploitation is in the wild";
  } else if (has.P && has.N) {
    color = "#d29922";
    label = "PoC claimed — verify whether exploit code is publicly available";
  } else {
    color = "#d29922";
    label = "Disagreement on exploitation status";
  }

  const sourceShort = (s) => s.replace("LLM/", "").replace("STATISTICAL/", "").replace("CISA/", "");

  return (
    <div style={{
      padding: "8px 12px", marginBottom: 8,
      background: "var(--bg-secondary)", borderRadius: 6,
      border: `1px solid ${color}`, fontSize: 12,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: color, display: "inline-block", flexShrink: 0 }} />
        <span style={{ fontWeight: 600, color }}>Exploitation:</span>
        <span style={{ color: "var(--text-secondary)" }}>{label}</span>
      </div>
      <div style={{ display: "flex", gap: 14, marginTop: 6, paddingLeft: 20, fontFamily: "monospace", flexWrap: "wrap" }}>
        {["A", "P", "N"].map((v) => groups[v] && (
          <span key={v}>
            <span style={{
              fontWeight: 700,
              background: v === "A" ? "rgba(248,81,73,0.15)" : v === "P" ? "rgba(240,136,62,0.15)" : "rgba(125,125,125,0.15)",
              color: v === "A" ? "#f85149" : v === "P" ? "#f0883e" : "var(--text-secondary)",
              padding: "1px 6px", borderRadius: 3,
            }}>
              {E_LABEL[v]}
            </span>
            <span style={{ color: "var(--text-muted)", marginLeft: 6 }}>
              ({groups[v].length}): {groups[v].map(sourceShort).join(", ")}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ── CWE Indicators ─────────────────────────────────────────────────────────

function findCweEntry(scores, sourcePrefix, modelPrefix) {
  if (!scores) return null;
  for (const [k, v] of Object.entries(scores)) {
    const lk = k.toLowerCase();
    const okSource = lk.startsWith(sourcePrefix.toLowerCase() + "/");
    const okModel = !modelPrefix || lk.includes(modelPrefix.toLowerCase());
    if (okSource && okModel && v?.value) {
      // Extract first CWE-NNN if details/value contain extra text
      const m = String(v.value).match(/CWE-\d+/i);
      if (m) return { key: k, cwe: m[0].toUpperCase() };
    }
  }
  return null;
}

function cweLLMAgreement(scores) {
  const gemma = findCweEntry(scores, "LLM", "gemma");
  const nemotron = findCweEntry(scores, "LLM", "nemotron");
  if (!gemma || !nemotron) return null;
  const match = gemma.cwe === nemotron.cwe;
  return {
    color: match ? "#3fb950" : "#f85149",
    label: match ? `Ready — both pick ${gemma.cwe}` : `Disagreement — ${gemma.cwe} vs ${nemotron.cwe}`,
    gemma, nemotron, match,
  };
}

function CweAgreementIndicator({ scores }) {
  const a = cweLLMAgreement(scores);
  if (!a) return null;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "8px 12px", marginBottom: 8,
      background: "var(--bg-secondary)", borderRadius: 6,
      border: `1px solid ${a.color}`, fontSize: 12,
    }}>
      <span style={{ width: 10, height: 10, borderRadius: "50%", background: a.color, display: "inline-block", flexShrink: 0 }} />
      <span style={{ fontWeight: 600, color: a.color }}>LLM Agreement:</span>
      <span style={{ color: "var(--text-secondary)" }}>{a.label}</span>
      <span style={{ color: "var(--text-muted)", marginLeft: "auto", fontFamily: "monospace" }}>
        {a.gemma.key.replace("LLM/", "")}: {a.gemma.cwe} · {a.nemotron.key.replace("LLM/", "")}: {a.nemotron.cwe}
      </span>
    </div>
  );
}

// ── Metric labels ─────────────────────────────────────────────────────────

const CVSS_METRIC_LABELS = {
  AV: "Attack Vector", AC: "Attack Complexity", PR: "Privileges Required",
  UI: "User Interaction", S: "Scope", C: "Confidentiality",
  I: "Integrity", A: "Availability",
  AT: "Attack Requirements",
  VC: "Vulnerable Confidentiality", VI: "Vulnerable Integrity", VA: "Vulnerable Availability",
  SC: "Subsequent Confidentiality", SI: "Subsequent Integrity", SA: "Subsequent Availability",
};
const SSVC_METRIC_LABELS = {
  E: "Exploitation", A: "Automatable", T: "Technical Impact", P: "Decision",
};

function metricLabel(metric, version) {
  const map = version === "ssvc" ? SSVC_METRIC_LABELS : CVSS_METRIC_LABELS;
  return map[metric] || metric;
}

// ── Score Matrix ──────────────────────────────────────────────────────────
// Tabular per-component comparison across all models, color-coded by
// agreement with the majority value for each metric.

// SSVC value normalization — CISA uses full words ("active"), LLM/ML use letters ("A")
const SSVC_VALUE_MAP = {
  E: { none: "N", poc: "P", active: "A", n: "N", p: "P", a: "A" },
  A: { no: "N", yes: "Y", n: "N", y: "Y" },
  T: { partial: "P", total: "T", p: "P", t: "T" },
};

const SSVC_OUTCOMES = {
  "N|N|P": "Track",  "N|N|T": "Track",  "N|Y|P": "Track",  "N|Y|T": "Track",
  "P|N|P": "Track",  "P|N|T": "Track*", "P|Y|P": "Track*", "P|Y|T": "Attend",
  "A|N|P": "Track*", "A|N|T": "Attend", "A|Y|P": "Attend", "A|Y|T": "Act",
};

function normalizeSsvcCell(metric, val) {
  if (!val) return val;
  const lower = String(val).toLowerCase();
  return SSVC_VALUE_MAP[metric]?.[lower] || val;
}

function ScoreMatrix({ scores, version = "3.1" }) {
  const [detailsSource, setDetailsSource] = useState(null);
  if (!scores || Object.keys(scores).length === 0) return null;

  const sources = Object.keys(scores).sort((a, b) => {
    // Pin truth sources (NVD/CISA/Vendor) and Vinnie's own score (gemma —
    // no composite for CVSS/SSVC) first, other LLMs next, STAT last
    const rank = (s) => {
      if (s === "NVD" || s.startsWith("Vendor") || s.startsWith("CISA")) return 0;
      if (s === VINNIE_LLM_KEY) return 1;
      if (s.startsWith("LLM/")) return 2;
      return 4;
    };
    return rank(a) - rank(b) || a.localeCompare(b);
  });

  // Collect unique metrics in order from first source that has them
  const metricOrder = [];
  const seen = new Set();
  for (const src of sources) {
    for (const c of (scores[src].components || [])) {
      if (!seen.has(c.metric)) {
        seen.add(c.metric);
        metricOrder.push(c.metric);
      }
    }
  }

  const isSsvc = version === "ssvc";

  // Build a metric -> {source -> short value} map and compute majorities
  const byMetric = {};
  // metric -> { short_value: label }  (collected from all sources for the legend)
  const valueLabels = {};
  for (const m of metricOrder) {
    byMetric[m] = {};
    valueLabels[m] = {};
  }
  for (const src of sources) {
    for (const c of (scores[src].components || [])) {
      const v = isSsvc ? normalizeSsvcCell(c.metric, c.short) : c.short;
      byMetric[c.metric][src] = v;
      if (c.label && !valueLabels[c.metric][v]) {
        valueLabels[c.metric][v] = c.label;
      }
    }
  }
  // For SSVC sources missing the P (decision) metric, compute it from E/A/T
  if (isSsvc && metricOrder.includes("P")) {
    for (const src of sources) {
      if (byMetric.P[src]) continue;
      const k = `${byMetric.E?.[src]}|${byMetric.A?.[src]}|${byMetric.T?.[src]}`;
      const decision = SSVC_OUTCOMES[k];
      if (decision) byMetric.P[src] = decision;
    }
  }
  // Compute majority excluding NVD (ground truth — shouldn't dominate model consensus)
  const consensusSources = sources.filter(s => s !== "NVD");
  const majority = {};
  for (const m of metricOrder) {
    const counts = {};
    for (const src of consensusSources) {
      const v = byMetric[m][src];
      if (v !== undefined) counts[v] = (counts[v] || 0) + 1;
    }
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    majority[m] = top ? top[0] : null;
  }

  const cellColor = (val, maj, total) => {
    if (val === undefined) return { bg: "transparent", color: "var(--text-muted)" };
    if (val === maj) return { bg: "rgba(63,185,80,0.18)", color: "#3fb950" };
    return { bg: "rgba(248,81,73,0.15)", color: "#f85149" };
  };

  const sourceShort = (s) => {
    let t = s.replace("LLM/", "").replace("STATISTICAL/", "");
    // Strip common version/quant suffixes for tighter columns
    t = t.replace(/-\d+b-it$/, "").replace(/-\d+b-instruct$/, "")
         .replace(/:\d+b$/, "").replace(/-\d+b$/, "");
    return t;
  };

  // Per-source agreement %: how many of its metrics match the majority (excluding NVD from majority)
  const agreement = {};
  for (const src of sources) {
    let match = 0, tot = 0;
    for (const m of metricOrder) {
      if (byMetric[m][src] !== undefined) {
        tot++;
        if (byMetric[m][src] === majority[m]) match++;
      }
    }
    agreement[src] = tot > 0 ? Math.round((match / tot) * 100) : 0;
  }

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ overflowX: "auto", display: "flex", justifyContent: "center" }}>
        <table style={{
          borderCollapse: "collapse", fontSize: 17,
          fontFamily: "monospace", lineHeight: 1.2,
        }}>
          <thead>
            <tr>
              <th style={{ padding: "5px 10px", textAlign: "left", borderBottom: "1px solid var(--border)", fontSize: 15 }}>
                Metric
              </th>
              {sources.map((s) => {
                const hasDetails = !!scores[s]?.details;
                return (
                  <th key={s} style={{
                    padding: "5px 10px", textAlign: "center", borderBottom: "1px solid var(--border)",
                    color: getSourceColor(s), fontSize: 15, whiteSpace: "nowrap", fontWeight: 600,
                  }}>
                    {sourceShort(s)}
                    {hasDetails && (
                      <button
                        onClick={() => setDetailsSource(detailsSource === s ? null : s)}
                        title="View reasoning"
                        style={{
                          marginLeft: 4, padding: "0 4px",
                          background: detailsSource === s ? "var(--accent)" : "transparent",
                          border: "1px solid var(--border)", borderRadius: 4,
                          color: "inherit", cursor: "pointer", fontSize: 12,
                        }}
                      >💬</button>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {metricOrder.map((m) => (
              <tr key={m}>
                <td style={{ padding: "3px 10px", fontWeight: 600, color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
                  {metricLabel(m, isSsvc ? "ssvc" : "cvss")}
                </td>
                {sources.map((s) => {
                  const val = byMetric[m][s];
                  const c = cellColor(val, majority[m]);
                  const fullLabel = val !== undefined ? valueLabels[m]?.[val] : null;
                  const display = fullLabel || val || "—";
                  return (
                    <td key={s} title={val !== undefined ? val : ""} style={{
                      padding: "3px 10px", textAlign: "center",
                      background: c.bg, color: c.color, fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}>
                      {display}
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr style={{ borderTop: "1px solid var(--border)", background: "var(--bg-secondary)" }}>
              <td style={{ padding: "5px 10px", fontWeight: 700, color: "var(--text-secondary)" }}>
                {isSsvc ? "Decision" : "Score"}
              </td>
              {sources.map((s) => {
                let display;
                if (isSsvc) {
                  display = byMetric.P?.[s] || "—";
                } else {
                  const score = scores[s].base_score;
                  display = typeof score === "number" ? score.toFixed(1) : "—";
                }
                // Only LLM-generated scores carry a confidence — NVD/CISA/ML rows skip it.
                const conf = scores[s]?.confidence;
                const confColor = conf === "High" ? "#3fb950" : conf === "Medium" ? "#d29922" : conf === "Low" ? "#f85149" : null;
                return (
                  <td key={s} style={{
                    padding: "5px 10px", textAlign: "center", fontWeight: 700,
                    color: getSourceColor(s),
                  }}>
                    {display}
                    {conf && (
                      <div style={{
                        fontSize: 10, fontWeight: 600, marginTop: 2,
                        textTransform: "uppercase", letterSpacing: "0.3px",
                        color: confColor,
                      }} title={`LLM confidence: ${conf}`}>
                        {conf}
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
            <tr style={{ background: "var(--bg-secondary)" }}>
              <td style={{ padding: "3px 10px", fontSize: 14, color: "var(--text-muted)" }}>
                Agreement
              </td>
              {sources.map((s) => {
                const pct = agreement[s];
                const color = pct >= 90 ? "#3fb950" : pct >= 75 ? "#d29922" : pct >= 50 ? "#f0883e" : "#f85149";
                return (
                  <td key={s} style={{
                    padding: "3px 10px", textAlign: "center", fontSize: 14,
                    color, fontWeight: 600,
                  }}>
                    {pct}%
                  </td>
                );
              })}
            </tr>
            <tr style={{ background: "var(--bg-secondary)" }}>
              <td style={{ padding: "3px 10px", fontSize: 13, color: "var(--text-muted)" }}>
                Scored
              </td>
              {sources.map((s) => {
                const ts = scores[s]?.timestamp;
                const rel = relTime(ts);
                return (
                  <td key={s} title={ts || ""} style={{
                    padding: "3px 10px", textAlign: "center", fontSize: 12,
                    color: "var(--text-muted)", fontFamily: "monospace",
                    lineHeight: 1.3,
                  }}>
                    {ts ? (
                      <>
                        <div>{ts.slice(0, 10)}</div>
                        <div style={{ fontSize: 10, opacity: 0.7 }}>{rel}</div>
                      </>
                    ) : "—"}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 14, color: "var(--text-muted)", marginTop: 6, padding: "0 4px", textAlign: "center" }}>
        <span style={{ background: "rgba(63,185,80,0.18)", color: "#3fb950", padding: "1px 6px", borderRadius: 3, fontWeight: 600 }}>green</span>
        {" = majority, "}
        <span style={{ background: "rgba(248,81,73,0.15)", color: "#f85149", padding: "1px 6px", borderRadius: 3, fontWeight: 600 }}>red</span>
        {" = differs. Agreement % = metrics matching majority (NVD excluded)."}
      </div>
      {detailsSource && scores[detailsSource]?.details && (
        <div style={{
          marginTop: 12, padding: 12, background: "var(--bg-secondary)",
          border: "1px solid var(--border)", borderRadius: 6,
        }}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
            <span style={{ color: getSourceColor(detailsSource), fontWeight: 600, fontSize: 14 }}>
              💬 {getSourceLabel(detailsSource)} reasoning
            </span>
            <button
              onClick={() => setDetailsSource(null)}
              style={{
                marginLeft: "auto", background: "transparent", border: "1px solid var(--border)",
                color: "var(--text-secondary)", padding: "2px 8px", borderRadius: 4, cursor: "pointer",
                fontSize: 12,
              }}
            >×</button>
          </div>
          <DetailsBody text={scores[detailsSource].details} />
        </div>
      )}
    </div>
  );
}

// ── Focus Areas ────────────────────────────────────────────────────────────
// Identify components where models disagree most — flag them for analyst review.

function FocusAreas({ scores, version = "3.1" }) {
  if (!scores) return null;
  // Exclude NVD from consensus calculation (it's ground truth, not a model vote)
  const sources = Object.keys(scores).filter(s => s !== "NVD");
  if (sources.length < 2) return null;

  const isSsvc = version === "ssvc";

  // Build per-metric counts: { metric: { value: [sources] } }
  const byMetric = {};
  const valueLabels = {};
  for (const src of sources) {
    for (const c of (scores[src].components || [])) {
      const v = isSsvc ? normalizeSsvcCell(c.metric, c.short) : c.short;
      if (!byMetric[c.metric]) byMetric[c.metric] = {};
      if (!valueLabels[c.metric]) valueLabels[c.metric] = {};
      if (!byMetric[c.metric][v]) byMetric[c.metric][v] = [];
      byMetric[c.metric][v].push(src);
      if (c.label && !valueLabels[c.metric][v]) valueLabels[c.metric][v] = c.label;
    }
  }
  // Compute SSVC P from E/A/T for sources missing it
  if (isSsvc && byMetric.E && byMetric.A && byMetric.T) {
    if (!byMetric.P) byMetric.P = {};
    for (const src of sources) {
      const e = Object.entries(byMetric.E).find(([_, ss]) => ss.includes(src))?.[0];
      const a = Object.entries(byMetric.A).find(([_, ss]) => ss.includes(src))?.[0];
      const t = Object.entries(byMetric.T).find(([_, ss]) => ss.includes(src))?.[0];
      const inP = Object.values(byMetric.P).some(ss => ss.includes(src));
      if (e && a && t && !inP) {
        const decision = SSVC_OUTCOMES[`${e}|${a}|${t}`];
        if (decision) {
          if (!byMetric.P[decision]) byMetric.P[decision] = [];
          byMetric.P[decision].push(src);
        }
      }
    }
  }

  // Score each metric by disagreement: 1 - (max_count / total)
  const ranked = Object.entries(byMetric).map(([metric, valMap]) => {
    const counts = Object.values(valMap).map(ss => ss.length);
    const total = counts.reduce((a, b) => a + b, 0);
    const max = Math.max(...counts);
    const disagreement = total > 0 ? 1 - max / total : 0;
    const splits = Object.entries(valMap)
      .map(([val, ss]) => ({ val, sources: ss, count: ss.length }))
      .sort((a, b) => b.count - a.count);
    return { metric, disagreement, splits, total };
  })
  .filter(m => m.disagreement >= 0.2 && m.splits.length >= 2)
  .sort((a, b) => b.disagreement - a.disagreement)
  .slice(0, 3);

  if (ranked.length === 0) {
    return (
      <div style={{
        marginTop: 12, padding: "10px 14px",
        background: "rgba(63,185,80,0.08)", borderRadius: 6,
        border: "1px solid rgba(63,185,80,0.3)", fontSize: 13,
        color: "var(--text-secondary)",
      }}>
        <span style={{ fontWeight: 600, color: "#3fb950" }}>✓ Strong consensus</span>
        {" — all models broadly agree. No specific components flagged for review."}
      </div>
    );
  }

  const sourceShort = (s) => s.replace("LLM/", "").replace("STATISTICAL/", "");

  return (
    <div style={{
      marginTop: 12, padding: "10px 14px",
      background: "rgba(240,136,62,0.08)", borderRadius: 6,
      border: "1px solid rgba(240,136,62,0.3)", fontSize: 13,
    }}>
      <div style={{ fontWeight: 600, color: "#f0883e", marginBottom: 8 }}>
        Focus areas — components most disputed across models
      </div>
      {ranked.map(({ metric, disagreement, splits }) => (
        <div key={metric} style={{ marginBottom: 8, paddingLeft: 4 }}>
          <div style={{ marginBottom: 3 }}>
            <span style={{ fontFamily: "monospace", fontWeight: 700, color: "var(--text-primary)" }}>
              {metric}
            </span>
            <span style={{ color: "var(--text-secondary)", marginLeft: 6 }}>
              {metricLabel(metric, version)}
            </span>
            <span style={{ color: "var(--text-muted)", marginLeft: 8, fontSize: 11 }}>
              ({Math.round(disagreement * 100)}% disagreement)
            </span>
          </div>
          <div style={{ paddingLeft: 12, fontSize: 12, color: "var(--text-secondary)" }}>
            {splits.map(({ val, sources, count }, i) => {
              const label = valueLabels[metric]?.[val];
              return (
                <div key={val} style={{ marginBottom: 2 }}>
                  <span style={{
                    fontFamily: "monospace", fontWeight: 700,
                    background: i === 0 ? "rgba(63,185,80,0.18)" : "rgba(248,81,73,0.15)",
                    color: i === 0 ? "#3fb950" : "#f85149",
                    padding: "1px 6px", borderRadius: 3,
                  }}>
                    {val}
                  </span>
                  {label && (
                    <span style={{ color: "var(--text-secondary)", marginLeft: 6, fontWeight: 500 }}>
                      {label}
                    </span>
                  )}
                  <span style={{ color: "var(--text-muted)", marginLeft: 6 }}>
                    ({count}): {sources.map(sourceShort).join(", ")}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <div style={{ marginTop: 6, fontSize: 11, color: "var(--text-muted)" }}>
        Review the source material for these metrics — the models gave conflicting reads.
      </div>
    </div>
  );
}

function timeAgo(isoDate) {
  if (!isoDate) return "";
  const diff = Date.now() - new Date(isoDate).getTime();
  const days = Math.floor(diff / 86400000);
  if (days < 0) return "";
  if (days === 0) return "today";
  if (days === 1) return "1 day ago";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months === 1) return "1 month ago";
  if (months < 12) return `${months} months ago`;
  const years = Math.floor(months / 12);
  return years === 1 ? "1 year ago" : `${years} years ago`;
}

const SCORE_TABS = [
  { key: "CVSSv3.1", label: "CVSS v3.1" },
  { key: "CVSSv4.0", label: "CVSS v4.0" },
  { key: "SSVC", label: "SSVC" },
  { key: "components", label: "Components" },
  { key: "CWE", label: "CWE" },
  { key: "refs", label: "References" },
  { key: "cpes", label: "CPEs" },
  { key: "prevalence", label: "Prevalence" },
  { key: "similar", label: "Similar" },
];

// ── Components Panel ─────────────────────────────────────────────────────
// Shows base vulnerability properties, derived CVSS/SSVC component values,
// and multi-tree SSVC decisions from the master analysis.

const PROPERTY_LABELS = {
  attack_vector: "Attack Vector",
  authentication: "Authentication",
  user_interaction: "User Interaction",
  attack_preconditions: "Attack Preconditions",
  confidentiality_scope: "Confidentiality Scope",
  integrity_scope: "Integrity Scope",
  availability_scope: "Availability Scope",
  cross_boundary_impact: "Cross-Boundary Impact",
  code_execution: "Code Execution",
  exploitation_status: "Exploitation Status",
  automatable: "Automatable",
  system_exposure: "System Exposure",
  mission_prevalence: "Mission Prevalence",
  public_wellbeing: "Public Well-Being",
  public_safety_impact: "Public Safety Impact",
  value_density: "Value Density",
};

const PROPERTY_GROUPS = [
  { title: "Attack Surface", keys: ["attack_vector", "authentication", "user_interaction", "attack_preconditions"] },
  { title: "Impact", keys: ["confidentiality_scope", "integrity_scope", "availability_scope", "cross_boundary_impact", "code_execution"] },
  { title: "SSVC Decision Points", keys: ["exploitation_status", "automatable", "system_exposure", "mission_prevalence", "public_wellbeing", "public_safety_impact", "value_density"] },
];

const DECISION_COLORS = {
  "Track": "#7ee787", "Track*": "#d29922", "Attend": "#f0883e", "Act": "#f85149",
  "Defer": "#7ee787", "Scheduled": "#d29922", "Out-of-Cycle": "#f0883e", "Immediate": "#f85149",
};

const DERIVED_LABELS = {
  cvss31: { title: "CVSS v3.1", metrics: ["AV", "AC", "PR", "UI", "S", "C", "I", "A"] },
  cvss40: { title: "CVSS v4.0", metrics: ["AV", "AC", "AT", "PR", "UI", "VC", "VI", "VA", "SC", "SI", "SA"] },
  ssvc: { title: "SSVC", metrics: ["E", "A", "T", "SE", "MP", "PW", "PSI"] },
};

function ComponentsPanel({ components }) {
  const [detailsKey, setDetailsKey] = useState(null);
  if (!components || Object.keys(components).length === 0) return null;

  const entries = Object.entries(components);
  const models = entries.map(([k]) => k.replace("LLM/", ""));
  const multi = entries.length > 1;

  // Collect all decision tree names across models
  const treeNames = [...new Set(entries.flatMap(([, d]) => Object.keys(d.decisions || {})))];

  // Compute majority for a set of values
  const majority = (vals) => {
    const counts = {};
    vals.forEach(v => { if (v != null) counts[v] = (counts[v] || 0) + 1; });
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    return top ? top[0] : null;
  };

  const cellBg = (val, maj, total) => {
    if (!multi || total < 2 || val == null) return "transparent";
    return val === maj ? "rgba(126,231,135,0.10)" : "rgba(248,81,73,0.10)";
  };

  // ── SSVC Decisions comparison ──
  const decisionRows = treeNames.map(tree => {
    const vals = entries.map(([, d]) => d.decisions?.[tree] || null);
    const maj = majority(vals);
    return { tree, vals, maj };
  });

  // ── Base properties comparison ──
  const allBaseKeys = [...new Set(entries.flatMap(([, d]) => Object.keys(d.base || {})))];
  const baseVals = (key) => {
    const vals = entries.map(([, d]) => {
      const v = d.base?.[key];
      return v == null ? null : Array.isArray(v) ? v.join(", ") : String(v);
    });
    return { vals, maj: majority(vals) };
  };

  // ── Derived components comparison ──
  const derivedMetricVals = (framework, metric) => {
    const vals = entries.map(([, d]) => d.derived?.[framework]?.[metric] ?? null);
    return { vals, maj: majority(vals) };
  };

  const thStyle = {
    padding: "6px 8px", fontSize: 11, fontWeight: 600, color: "var(--text-muted)",
    textAlign: "center", whiteSpace: "nowrap",
  };
  const tdLabel = { padding: "6px 8px", color: "var(--text-secondary)", whiteSpace: "nowrap" };
  const tdVal = (bg) => ({
    padding: "6px 8px", fontFamily: "monospace", fontWeight: 500, textAlign: "center",
    background: bg,
  });
  const sectionHdr = {
    fontSize: 12, fontWeight: 600, color: "var(--text-secondary)",
    textTransform: "uppercase", letterSpacing: "0.05em",
  };

  return (
    <div>
      {multi && (
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8 }}>
          {entries.length} model(s): {models.join(", ")}
        </div>
      )}

      {/* SSVC Decisions */}
      {treeNames.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ ...sectionHdr, marginBottom: 8 }}>SSVC Decisions</div>
          {multi ? (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--border)" }}>
                    <th style={{ ...thStyle, textAlign: "left" }}>Tree</th>
                    {models.map(m => (
                      <th key={m} style={thStyle}>{m}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {decisionRows.map(({ tree, vals, maj }) => (
                    <tr key={tree} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ ...tdLabel, textTransform: "capitalize" }}>{tree}</td>
                      {vals.map((v, i) => (
                        <td key={i} style={{
                          ...tdVal(cellBg(v, maj, vals.filter(x => x).length)),
                          color: DECISION_COLORS[v] || "var(--text-primary)",
                          fontWeight: 700,
                        }}>
                          {v || "—"}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ ...tdLabel, fontStyle: "italic" }}>Confidence</td>
                    {entries.map(([k, d], i) => {
                      const c = d.confidence;
                      return (
                        <td key={i} style={{
                          ...tdVal("transparent"), fontSize: 12,
                          color: c === "High" ? "#7ee787" : c === "Medium" ? "#d29922" : "#f85149",
                        }}>
                          {c || "—"}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
              {decisionRows.map(({ tree, vals }) => (
                <div key={tree} style={{
                  padding: "10px 16px", borderRadius: 6,
                  background: "var(--bg-secondary)", border: "1px solid var(--border)",
                  textAlign: "center", minWidth: 120,
                }}>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "capitalize", marginBottom: 4 }}>
                    {tree}
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: DECISION_COLORS[vals[0]] || "var(--text-primary)" }}>
                    {vals[0] || "—"}
                  </div>
                </div>
              ))}
              {entries[0]?.[1]?.confidence && (
                <span style={{
                  fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10,
                  color: entries[0][1].confidence === "High" ? "#7ee787" : entries[0][1].confidence === "Medium" ? "#d29922" : "#f85149",
                  background: entries[0][1].confidence === "High" ? "rgba(126,231,135,0.12)" : entries[0][1].confidence === "Medium" ? "rgba(210,153,34,0.12)" : "rgba(248,81,73,0.12)",
                }}>
                  {entries[0][1].confidence} confidence
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Base Properties */}
      {allBaseKeys.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ ...sectionHdr, marginBottom: 6 }}>Base Properties</div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid var(--border)" }}>
                  <th style={{ ...thStyle, textAlign: "left" }}>Property</th>
                  {entries.map(([key, d]) => (
                    <th key={key} style={thStyle}>
                      {key.replace("LLM/", "")}
                      {d.details && (
                        <button
                          onClick={() => setDetailsKey(detailsKey === key ? null : key)}
                          title="View reasoning"
                          style={{
                            marginLeft: 4, padding: "0 4px",
                            background: detailsKey === key ? "var(--accent)" : "transparent",
                            border: "1px solid var(--border)", borderRadius: 4,
                            color: "inherit", cursor: "pointer", fontSize: 12,
                          }}
                        >💬</button>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PROPERTY_GROUPS.map(group => {
                  const visibleKeys = group.keys.filter(k => allBaseKeys.includes(k));
                  if (visibleKeys.length === 0) return null;
                  return [
                    <tr key={`hdr-${group.title}`}>
                      <td colSpan={multi ? models.length + 1 : 2} style={{
                        padding: "8px 8px 4px", fontSize: 11, fontWeight: 600,
                        color: "var(--text-muted)", textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}>
                        {group.title}
                      </td>
                    </tr>,
                    ...visibleKeys.map(k => {
                      const { vals, maj } = baseVals(k);
                      return (
                        <tr key={k} style={{ borderBottom: "1px solid var(--border)" }}>
                          <td style={tdLabel}>{PROPERTY_LABELS[k] || k}</td>
                          {multi ? vals.map((v, i) => (
                            <td key={i} style={tdVal(cellBg(v, maj, vals.filter(x => x).length))}>
                              {v || "—"}
                            </td>
                          )) : (
                            <td style={{ padding: "6px 8px", fontFamily: "monospace", fontWeight: 500 }}>
                              {vals[0] || "—"}
                            </td>
                          )}
                        </tr>
                      );
                    }),
                  ];
                })}
              </tbody>
            </table>
          </div>
          {detailsKey && components[detailsKey]?.details && (
            <div
              onClick={() => setDetailsKey(null)}
              style={{
                position: "fixed", inset: 0, zIndex: 1100,
                background: "rgba(0,0,0,0.55)",
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: 24,
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: "var(--bg-primary)",
                  border: "1px solid var(--border)", borderRadius: 8,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
                  width: "min(760px, 92vw)", maxHeight: "82vh",
                  display: "flex", flexDirection: "column",
                }}
              >
                <div style={{
                  display: "flex", alignItems: "center", padding: "12px 16px",
                  borderBottom: "1px solid var(--border)", flexShrink: 0,
                }}>
                  <span style={{ color: "var(--text-secondary)", fontWeight: 600, fontSize: 14 }}>
                    💬 {detailsKey.replace("LLM/", "")} reasoning
                  </span>
                  <button
                    onClick={() => setDetailsKey(null)}
                    style={{
                      marginLeft: "auto", background: "transparent", border: "1px solid var(--border)",
                      color: "var(--text-secondary)", padding: "2px 10px", borderRadius: 4,
                      cursor: "pointer", fontSize: 14,
                    }}
                  >×</button>
                </div>
                <div style={{ padding: 16, overflowY: "auto" }}>
                  <DetailsBody text={components[detailsKey].details} />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Derived Components */}
      {Object.keys(DERIVED_LABELS).some(fw => entries.some(([, d]) => d.derived?.[fw])) && (
        <div>
          <div style={{ ...sectionHdr, marginBottom: 6 }}>Derived Components</div>
          {Object.entries(DERIVED_LABELS).map(([fw, { title, metrics }]) => {
            const hasAny = entries.some(([, d]) => d.derived?.[fw]);
            if (!hasAny) return null;
            return (
              <div key={fw} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 4 }}>
                  {title}
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                    {multi && (
                      <thead>
                        <tr style={{ borderBottom: "2px solid var(--border)" }}>
                          <th style={{ ...thStyle, textAlign: "left" }}>Metric</th>
                          {models.map(m => (
                            <th key={m} style={thStyle}>{m}</th>
                          ))}
                        </tr>
                      </thead>
                    )}
                    <tbody>
                      {metrics.map(m => {
                        const { vals, maj } = derivedMetricVals(fw, m);
                        if (vals.every(v => v == null)) return null;
                        return (
                          <tr key={m} style={{ borderBottom: "1px solid var(--border)" }}>
                            <td style={tdLabel}>
                              <span style={{ color: "var(--text-muted)" }}>{m}</span>
                              {CVSS_METRIC_LABELS[m] && (
                                <span style={{ color: "var(--text-secondary)", marginLeft: 6, fontSize: 11 }}>
                                  {CVSS_METRIC_LABELS[m]}
                                </span>
                              )}
                            </td>
                            {multi ? vals.map((v, i) => (
                              <td key={i} style={tdVal(cellBg(v, maj, vals.filter(x => x).length))}>
                                {v ?? "—"}
                              </td>
                            )) : (
                              <td style={{ padding: "6px 8px", fontFamily: "monospace", fontWeight: 500 }}>
                                {vals[0] ?? "—"}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function SearchPage() {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [scoreTab, setScoreTab] = useState("CVSSv3.1");
  const [recentBuckets, setRecentBuckets] = useState({ "2h": [], "1d": [], "1w": [] });
  const [recentLoading, setRecentLoading] = useState(true);
  const [similarCves, setSimilarCves] = useState(null);
  const [similarLoading, setSimilarLoading] = useState(false);
  const [prevalence, setPrevalence] = useState(null);
  const [prevalenceLoading, setPrevalenceLoading] = useState(false);
  const [prevalenceError, setPrevalenceError] = useState(null);
  const [cweDetailsSource, setCweDetailsSource] = useState(null);

  useEffect(() => {
    fetchRecentBuckets(15)
      .then(setRecentBuckets)
      .catch(() => {})
      .finally(() => setRecentLoading(false));
    // Deep link: /cve/CVE-YYYY-NNNNN
    const m = window.location.pathname.match(/^\/cve\/(CVE-\d{4}-\d+)$/i);
    if (m) {
      const cveId = m[1].toUpperCase();
      setQuery(cveId);
      loadCveDetail(cveId);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isExactCve = (s) => /^CVE-\d{4}-\d{4,}$/i.test(s.trim());

  const loadCveDetail = async (cveId) => {
    // Update URL without reload
    window.history.pushState(null, "", `/cve/${cveId}`);
    setLoading(true);
    setError(null);
    setSimilarCves(null);
    setPrevalence(null);
    setPrevalenceError(null);
    setResult(null);
    setSearchResults(null);
    try {
      const data = await fetchCve(cveId);
      const flatScores = [];
      for (const [src, info] of Object.entries(data.scores || {})) {
        flatScores.push({ ...info, source: src, type: "CVSSv3.1" });
      }
      for (const [src, info] of Object.entries(data.cvss4 || {})) {
        flatScores.push({ ...info, source: src, type: "CVSSv4.0" });
      }
      for (const [src, info] of Object.entries(data.ssvc || {})) {
        flatScores.push({ ...info, source: src, type: "SSVC" });
      }
      for (const [src, info] of Object.entries(data.cwe || {})) {
        flatScores.push({ ...info, source: src, type: "CWE" });
      }
      data.flatScores = flatScores;
      // Compute last AI scoring time (LLM/COMPOSITE only — exclude static imports)
      const aiSources = flatScores
        .filter(s => /^(LLM|ML|COMPOSITE)/.test(s.source) && s.timestamp)
        .map(s => s.timestamp);
      data.last_scored_at = aiSources.length > 0 ? aiSources.sort().reverse()[0] : null;
      setResult(data);
    } catch (err) {
      setError(err.message || "CVE not found");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const q = query.trim().toUpperCase();
    if (q.length < 4) return;

    if (isExactCve(q)) {
      await loadCveDetail(q);
    } else {
      // Substring search
      setLoading(true);
      setError(null);
      setResult(null);
      setSearchResults(null);
      try {
        const data = await fetchCves({ search: q, perPage: 100 });
        if (data.cves && data.cves.length === 1) {
          // Single match — go straight to detail
          await loadCveDetail(data.cves[0].cve);
        } else if (data.cves && data.cves.length > 0) {
          setSearchResults(data.cves);
        } else {
          setError("No CVEs found");
        }
      } catch (err) {
        setError(err.message || "Search failed");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div style={{ maxWidth: 1350, margin: "0 auto" }}>
      <form onSubmit={handleSearch} style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="CVE-2025-9943"
          maxLength={20}
          style={{
            flex: 1, padding: "10px 14px", fontSize: 16,
            background: "var(--bg-secondary)", border: "1px solid var(--border)",
            borderRadius: 6, color: "var(--text-primary)",
          }}
          autoFocus
        />
        <button
          type="submit"
          disabled={loading || query.trim().length < 4}
          style={{
            padding: "10px 24px", fontSize: 14, fontWeight: 600,
            background: "#238636", color: "#fff", border: "none",
            borderRadius: 6, cursor: "pointer",
          }}
        >
          {loading ? "..." : "Search"}
        </button>
      </form>

      {error && <div style={{ color: "#f85149", marginBottom: 16 }}>{error}</div>}

      {loading && (
        <div style={{ marginBottom: 20, color: "var(--text-muted)", fontSize: 13 }}>
          Loading...
        </div>
      )}

      {!result && !searchResults && !loading && recentLoading && (
        <div style={{ marginBottom: 20, color: "var(--text-muted)", fontSize: 13 }}>
          Loading recent CVEs...
        </div>
      )}

      {!result && !searchResults && !loading && !recentLoading && (
        (recentBuckets["2h"].length + recentBuckets["1d"].length + recentBuckets["1w"].length) > 0 && (
        <div style={{ marginBottom: 20 }}>
          {[
            { key: "2h", label: "Recently Scored — Last 2h" },
            { key: "1d", label: "Recently Scored — Last 1d" },
            { key: "1w", label: "Recently Scored — Last 1w" },
          ].filter((b) => recentBuckets[b.key] && recentBuckets[b.key].length > 0).map((b) => (
            <div key={b.key} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {b.label} ({recentBuckets[b.key].length})
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {recentBuckets[b.key].map((cve) => (
                  <button
                    key={cve.cve}
                    onClick={() => { setQuery(cve.cve); loadCveDetail(cve.cve); }}
                    title={cve.updated_at ? `Scored ${fmtTs(cve.updated_at)}` : ""}
                    style={{
                      background: "var(--bg-secondary)", border: "1px solid var(--border)",
                      color: "#58a6ff", padding: "6px 12px", borderRadius: 6,
                      fontSize: 13, cursor: "pointer",
                    }}
                  >
                    {cve.cve}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        )
      )}

      {searchResults && (
        <div className="panel" style={{ marginBottom: 16 }}>
          <div className="panel-header">{searchResults.length} matches</div>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid var(--border)" }}>
                <th style={{ textAlign: "left", padding: "6px 8px" }}>CVE</th>
                <th style={{ textAlign: "left", padding: "6px 8px" }}>Scores</th>
                <th style={{ textAlign: "right", padding: "6px 8px" }}>Refs</th>
              </tr>
            </thead>
            <tbody>
              {searchResults.map((cve) => {
                const scoreCount = Object.keys(cve.scores || {}).length
                  + Object.keys(cve.ssvc || {}).length
                  + Object.keys(cve.cvss4 || {}).length
                  + Object.keys(cve.cwe || {}).length;
                return (
                  <tr
                    key={cve.cve}
                    style={{ borderBottom: "1px solid var(--border)", cursor: "pointer" }}
                    onClick={() => { setQuery(cve.cve); setSearchResults(null); loadCveDetail(cve.cve); }}
                  >
                    <td style={{ padding: "6px 8px", color: "#58a6ff" }}>{cve.cve}</td>
                    <td style={{ padding: "6px 8px", color: "var(--text-secondary)" }}>{scoreCount} scores</td>
                    <td style={{ padding: "6px 8px", textAlign: "right", color: "var(--text-secondary)" }}>
                      {cve.ref_cached}/{cve.ref_total}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {result && (
        <div>
          <div className="panel" style={{ marginBottom: 12 }}>
            <div className="panel-header" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {result.cve}
              <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                <button
                  className="raw-json-link"
                  onClick={() => window.open(`https://nvd.nist.gov/vuln/detail/${result.cve}`, "_blank")}
                >NVD</button>
                <button
                  className="raw-json-link"
                  title="Download CSAF 2.1 advisory"
                  onClick={() => downloadCsaf(result.cve).catch((e) => alert(e.message))}
                >CSAF</button>
                <button
                  className="raw-json-link"
                  title="View on Vincent"
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "3px 10px" }}
                  onClick={() => window.open(`https://vincent.vulcoord.net/score/?cve=${result.cve}&analyze=1`, "_blank")}
                >
                  <img src="/vincent.png" alt="" style={{ width: 18, height: 18 }} />
                  VINCENT
                </button>
              </div>
            </div>
            {result.affected && result.affected.length > 0 && (
              <div style={{ fontSize: 13, color: "var(--text-secondary)", margin: "6px 0" }}>
                {result.affected.map((a, i) => (
                  <span key={i} style={{ marginRight: 12 }}>
                    <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>{a.vendor}</span>
                    {a.product && <span> / {a.product}</span>}
                  </span>
                ))}
              </div>
            )}
            {result.cna && (
              <div style={{ fontSize: 11, color: "var(--text-muted)", margin: "4px 0", display: "flex", gap: 16, flexWrap: "wrap" }}>
                {result.cna.cna && (
                  <span title={result.cna.org_id ? `Assigner org ${result.cna.org_id}` : undefined}>
                    CNA: <span style={{ color: "var(--text-primary)", fontWeight: 600 }}>{result.cna.cna}</span>
                  </span>
                )}
                {result.cna.state && result.cna.state !== "PUBLISHED" && (
                  <span style={{ color: "#f0883e", fontWeight: 600 }}>{result.cna.state}</span>
                )}
                {result.cna.reserved_at && (
                  <span title={result.cna.reserved_at}>Reserved: {fmtDate(result.cna.reserved_at)}</span>
                )}
                {result.cna.cna_updated_at && (
                  <span title={result.cna.cna_updated_at}>
                    CNA record: {fmtTs(result.cna.cna_updated_at)}{" "}
                    <span style={{ color: "var(--text-secondary)" }}>({timeAgo(result.cna.cna_updated_at)})</span>
                  </span>
                )}
                {result.cna.adp && result.cna.adp.length > 0 && (
                  <span>Enriched by: <span style={{ color: "var(--text-secondary)" }}>{result.cna.adp.join(", ")}</span></span>
                )}
              </div>
            )}
            {(result.published_at || result.modified_at || result.last_scored_at) && (
              <div style={{ fontSize: 11, color: "var(--text-muted)", margin: "4px 0", display: "flex", gap: 16 }}>
                {result.published_at && <span title={result.published_at}>Published: {fmtTs(result.published_at)} <span style={{ color: "var(--text-secondary)" }}>({timeAgo(result.published_at)})</span></span>}
                {result.modified_at && <span title={result.modified_at}>Modified: {fmtTs(result.modified_at)} <span style={{ color: "var(--text-secondary)" }}>({timeAgo(result.modified_at)})</span></span>}
                {result.last_scored_at
                  ? <span title={result.last_scored_at}>Scored: {fmtTs(result.last_scored_at)} <span style={{ color: "var(--text-secondary)" }}>({timeAgo(result.last_scored_at)})</span></span>
                  : <span style={{ color: "#f0883e" }}>Not yet AI-scored</span>}
              </div>
            )}
            {result.description && (
              <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: "8px 0" }}>
                {result.description}
              </p>
            )}
          </div>

          {/* Score type tabs */}
          <div className="table-tabs" style={{ marginBottom: 0 }}>
            {SCORE_TABS.map(({ key, label }) => {
              const count = key === "refs"
                ? (result.references || []).length
                : key === "cpes"
                ? ((result.cpes || []).length
                    + (result.cpes_suggested || []).length
                    + (result.cpes_llm || []).length)
                : key === "similar"
                ? (similarCves ? similarCves.length : "?")
                : key === "prevalence"
                ? (prevalence ? (prevalence.products || []).length : "?")
                : key === "components"
                ? Object.keys(result.components || {}).length
                : (result.flatScores || []).filter((s) => s.type === key).length;
              if (key !== "similar" && key !== "prevalence" && count === 0) return null;
              return (
                <button
                  key={key}
                  className={`table-tab ${scoreTab === key ? "active" : ""}`}
                  onClick={() => {
                    setScoreTab(key);
                    if (key === "prevalence" && !prevalence && result) {
                      setPrevalenceLoading(true);
                      setPrevalenceError(null);
                      fetchCvePrevalence(result.cve)
                        .then(setPrevalence)
                        .catch((err) => setPrevalenceError(err.message || "Prevalence unavailable"))
                        .finally(() => setPrevalenceLoading(false));
                    }
                    if (key === "similar" && !similarCves && result) {
                      setSimilarLoading(true);
                      fetchSimilar(result.cve, 10)
                        .then((d) => setSimilarCves(d.similar || []))
                        .catch(() => setSimilarCves([]))
                        .finally(() => setSimilarLoading(false));
                    }
                  }}
                >
                  {label} ({count})
                </button>
              );
            })}
          </div>

          <div className="panel" style={{ marginBottom: 12, borderTopLeftRadius: 0 }}>
            {scoreTab === "CVSSv3.1" && result.scores && (
              <>
                <AgreementIndicator scores={result.scores} />
                <CvssRadarChart cveData={result} visibleSources={Object.keys(result.scores)} />
                <ScoreMatrix scores={result.scores} version="3.1" />
                <FocusAreas scores={result.scores} version="3.1" />
              </>
            )}
            {scoreTab === "CVSSv4.0" && result.cvss4 && (
              <>
                <AgreementIndicator scores={result.cvss4} />
                <Cvss4RadarChart cveData={result} />
                <ScoreMatrix scores={result.cvss4} version="4.0" />
                <FocusAreas scores={result.cvss4} version="4.0" />
              </>
            )}
            {scoreTab === "SSVC" && result.ssvc && (
              <>
                <SsvcAgreementIndicator scores={result.ssvc} />
                <SsvcExploitationIndicator scores={result.ssvc} />
                <SsvcRadarChart ssvcData={result.ssvc} />
                <ScoreMatrix scores={result.ssvc} version="ssvc" />
                <FocusAreas scores={result.ssvc} version="ssvc" />
              </>
            )}
            {scoreTab === "components" && result.components && (
              <ComponentsPanel components={result.components} />
            )}
            {scoreTab === "CWE" && result.cwe && (
              <>
                <CweAgreementIndicator scores={result.cwe} />
                <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--border)" }}>
                    <th style={{ textAlign: "left", padding: "8px" }}>Source</th>
                    <th style={{ textAlign: "left", padding: "8px" }}>CWE</th>
                    <th style={{ textAlign: "left", padding: "8px" }}>Name</th>
                    <th style={{ textAlign: "left", padding: "8px" }}>Confidence</th>
                    <th style={{ textAlign: "left", padding: "8px" }}>Scored</th>
                    <th style={{ textAlign: "left", padding: "8px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(result.cwe).map(([src, info]) => {
                    const name = info.details?.match(/CWE-\d+[:\s\-]+([^\n(]+)/)?.[1]?.trim().replace(/\*+/g, "").replace(/\.$/, "") || "";
                    const conf = info.confidence;
                    const confColor = conf === "High" ? "#3fb950" : conf === "Medium" ? "#d29922" : conf === "Low" ? "#f85149" : null;
                    const hasDetails = !!info.details;
                    return (
                      <tr key={src} style={{ borderBottom: "1px solid var(--border)" }}>
                        <td style={{ padding: "8px" }}>
                          <span style={{ color: getSourceColor(src), fontWeight: 600 }}>{getSourceLabel(src)}</span>
                        </td>
                        <td style={{ padding: "8px", fontWeight: 600, fontFamily: "monospace" }}>{info.value}</td>
                        <td style={{ padding: "8px", color: "var(--text-secondary)" }}>{name}</td>
                        <td style={{ padding: "8px" }}>
                          {conf && (
                            <span style={{
                              fontSize: 11, fontWeight: 600, textTransform: "uppercase",
                              letterSpacing: "0.3px", color: confColor,
                            }}>
                              {conf}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "8px", fontSize: 11, color: "var(--text-muted)", fontFamily: "monospace" }} title={info.timestamp || ""}>
                          {info.timestamp ? `${fmtTs(info.timestamp)} (${relTime(info.timestamp)})` : ""}
                        </td>
                        <td style={{ padding: "8px" }}>
                          {hasDetails && (
                            <button
                              onClick={() => setCweDetailsSource(cweDetailsSource === src ? null : src)}
                              title="View reasoning"
                              style={{
                                padding: "0 4px",
                                background: cweDetailsSource === src ? "var(--accent)" : "transparent",
                                border: "1px solid var(--border)", borderRadius: 4,
                                color: "inherit", cursor: "pointer", fontSize: 12,
                              }}
                            >💬</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {cweDetailsSource && result.cwe[cweDetailsSource]?.details && (
                <div style={{
                  marginTop: 12, padding: 12, background: "var(--bg-secondary)",
                  border: "1px solid var(--border)", borderRadius: 6,
                }}>
                  <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ color: getSourceColor(cweDetailsSource), fontWeight: 600, fontSize: 14 }}>
                      💬 {getSourceLabel(cweDetailsSource)} reasoning
                    </span>
                    <button
                      onClick={() => setCweDetailsSource(null)}
                      style={{
                        marginLeft: "auto", background: "transparent", border: "1px solid var(--border)",
                        color: "var(--text-secondary)", padding: "2px 8px", borderRadius: 4, cursor: "pointer",
                        fontSize: 12,
                      }}
                    >×</button>
                  </div>
                  <DetailsBody text={result.cwe[cweDetailsSource].details} />
                </div>
              )}
              </>
            )}
            {scoreTab === "refs" && (
              <ReferencesPanel references={result.references} />
            )}
            {scoreTab === "cpes" && (
              <CpesPanel
                cpes={result.cpes}
                cpesSuggested={result.cpes_suggested}
                cpesLlm={result.cpes_llm}
              />
            )}
            {scoreTab === "prevalence" && (
              <PrevalencePanel
                data={prevalence}
                loading={prevalenceLoading}
                error={prevalenceError}
                cve={result.cve}
              />
            )}
            {scoreTab === "similar" && (
              <div style={{ padding: 8 }}>
                {similarLoading && <div style={{ color: "var(--text-muted)" }}>Loading similar CVEs...</div>}
                {similarCves && similarCves.length === 0 && !similarLoading && (
                  <div style={{ color: "var(--text-muted)" }}>No similar CVEs found</div>
                )}
                {similarCves && similarCves.length > 0 && (
                  <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ borderBottom: "2px solid var(--border)" }}>
                        <th style={{ textAlign: "left", padding: "6px 8px" }}>CVE</th>
                        <th style={{ textAlign: "left", padding: "6px 8px" }}>Score</th>
                        <th style={{ textAlign: "left", padding: "6px 8px" }}>Severity</th>
                        <th style={{ textAlign: "left", padding: "6px 8px" }}>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {similarCves.map((s) => (
                        <tr
                          key={s.cve_id}
                          style={{ borderBottom: "1px solid var(--border)", cursor: "pointer" }}
                          onClick={() => { setQuery(s.cve_id); setSimilarCves(null); loadCveDetail(s.cve_id); }}
                        >
                          <td style={{ padding: "6px 8px", color: "#58a6ff", whiteSpace: "nowrap" }}>{s.cve_id}</td>
                          <td style={{ padding: "6px 8px", fontWeight: 600 }}>{s.nvd_score}</td>
                          <td style={{ padding: "6px 8px", fontSize: 11, textTransform: "uppercase" }}>{s.nvd_severity}</td>
                          <td style={{ padding: "6px 8px", color: "var(--text-secondary)", fontSize: 12 }}>
                            {s.description_snippet}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
